import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Select } from 'antd';
import FeatherIcon from '../../common/FeatherIcon';
import { freshChanges, taskerKey } from './model';

const popupContainer = trigger => trigger.parentElement;

export default function DefaultsDrawer({ catalog, selected, onSelect, changes, onChanges,
  options, labels, eligibleOptions, names, preview, busy, error,
  onPreview, onSave, onBack, onClose, onCopy }) {
  const panel = useRef(null), errorRef = useRef(null), state = useRef({ busy, onClose });
  state.current = { busy, onClose };
  useEffect(() => {
    const opener = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panel.current?.focus();
    const keydown = event => {
      if (event.key === 'Escape') {
        // Let an open tasker/property dropdown consume its first Escape.
        if (panel.current?.querySelector('.ant-select-open')) return;
        event.preventDefault();
        if (!state.current.busy) state.current.onClose();
      }
      if (event.key !== 'Tab') return;
      const controls = [...panel.current.querySelectorAll('button:not(:disabled), input:not(:disabled):not([type="hidden"]), select:not(:disabled), a[href], [tabindex="0"]')]
        .filter(el => !el.closest('[hidden]') && el.getClientRects().length);
      const first = controls[0], last = controls[controls.length - 1];
      if (!first) { event.preventDefault(); panel.current.focus(); }
      else if (event.shiftKey && (document.activeElement === first || document.activeElement === panel.current)) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !panel.current.contains(document.activeElement))) {
        event.preventDefault(); first.focus();
      }
    };
    document.addEventListener('keydown', keydown);
    return () => {
      document.removeEventListener('keydown', keydown);
      document.body.style.overflow = previousOverflow;
      if (opener?.isConnected) opener.focus();
    };
  }, []);
  useEffect(() => { if (error) { errorRef.current?.focus(); errorRef.current?.scrollIntoView?.({block:'nearest'}); } }, [error]);

  const change = (index, patch) => onChanges(changes.map((row, i) => i === index ? {...row, ...patch} : row));
  const valid = selected.length > 0 && changes.length > 0 && changes.every(row =>
    row.assignees.length > 0 || row.allowEmpty);
  const propertyLabel = id => {
    const property = catalog.properties.find(p => String(p.rental_id) === String(id));
    return <><div className="semi-bold-font">{property?.pms_id || `Property #${id}`}</div>
      {property?.address && <div className="secondary">{property.address}</div>}</>;
  };
  const serviceRow = service_key => {
    const saved=selected.length===1?catalog.defaults.find(d=>String(d.rental_id)===String(selected[0])&&d.service_key===service_key):null;
    return {...freshChanges()[0],service_key,assignees:saved?.assignees||[],allowEmpty:!!saved?.assignees?.length};
  };
  const addService = () => {
    const next = options.find(option => !changes.some(row => row.service_key === option.value));
    if (next) onChanges([...changes, serviceRow(next.value)]);
  };

  return createPortal(<div className="rbr-property-defaults rbr-defaults-offcanvas"
    onClick={event => { if (event.target === event.currentTarget && !busy) onClose(); }}>
    <div ref={panel} className="hms-side-drawer hms-modals rbr-defaults-drawer" role="dialog" aria-modal="true"
      aria-labelledby="defaults-drawer-title" aria-busy={busy} tabIndex={-1}>
      <div className="uk-modal-header rbr-defaults-drawer-header">
        <h2 id="defaults-drawer-title" className="uk-modal-title">{preview ? 'Review Default Changes' : 'Set Property Defaults'}</h2>
        <button type="button" className="uk-offcanvas-close uk-close-large" aria-label="Close property defaults" disabled={busy} onClick={onClose}>
          <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.5" /></svg>
        </button>
      </div>
      <div className="rbr-defaults-drawer-body">
        {error && <div ref={errorRef} role="alert" tabIndex={-1} className="uk-alert uk-alert-danger">{error}</div>}
        {!preview ? <>
          {!valid && <p className="secondary">{!selected.length?'Choose at least one property to continue.':!changes.length?'Add a service to continue.':'Choose taskers for each service to continue.'}</p>}
          <h3>Properties</h3>
          <p className="uk-text-meta">{selected.length} properties selected. Review your changes before saving.</p>
          <label className="uk-form-label" htmlFor="defaults-properties">Properties</label>
          <Select id="defaults-properties" aria-label="Properties" mode="multiple" showSearch optionFilterProp="label"
            getPopupContainer={popupContainer} maxTagCount={2} disabled={busy} value={selected} onChange={onSelect}
            placeholder="Choose properties" options={catalog.properties.map(p=>({value:Number(p.rental_id),label:p.pms_id || `Property #${p.rental_id}`}))} />
          <h3 className="uk-margin-top">Services and Taskers</h3>
          <p className="uk-text-meta">Choose the taskers you want for each service. These values replace existing defaults on the selected properties.</p>
          <details className="rbr-defaults-copy">
            <summary>Copy from another property</summary>
            <label className="uk-form-label" htmlFor="defaults-copy">Copy settings from a property</label>
            <Select id="defaults-copy" aria-label="Copy settings from a property" showSearch optionFilterProp="label"
              getPopupContainer={popupContainer} disabled={busy} placeholder="Choose a property" onChange={onCopy}
              options={catalog.properties.map(p=>({value:p.rental_id,label:p.pms_id || `Property #${p.rental_id}`}))} />
          </details>
          {changes.map((row,index)=><div className="rbr-defaults-change" key={index}>
            <div className="rbr-defaults-change-heading"><strong>Service {index+1}</strong><button type="button" disabled={busy}
              aria-label={`Remove service ${index+1}`} title="Remove service" className="uk-button rbr-defaults-remove-service" onClick={()=>onChanges(changes.filter((_,i)=>i!==index))}>
              <FeatherIcon name="trash-2" />
            </button></div>
            <label className="uk-form-label" htmlFor={`defaults-service-${index}`}>Service</label>
                <select id={`defaults-service-${index}`} aria-label={`Service ${index+1}`} className="uk-select" disabled={busy} value={row.service_key} onChange={e=>change(index,serviceRow(e.target.value))}>
                  {['Task types','Specialist services (task category)','Checklist exceptions'].map(group=><optgroup key={group} label={group}>
                    {options.filter(option=>option.group===group && !changes.some((other,i)=>i!==index && other.service_key===option.value)).map(option=><option key={option.value} value={option.value}>{option.label}</option>)}
                  </optgroup>)}
                </select>
            <label className="uk-form-label rbr-defaults-taskers-label" htmlFor={`defaults-taskers-${index}`}>Taskers</label>
              <Select id={`defaults-taskers-${index}`} aria-label={`Taskers ${index+1}`} mode="multiple" showSearch optionFilterProp="label" getPopupContainer={popupContainer}
                disabled={busy} placeholder="Choose users or contacts" value={row.assignees.map(taskerKey)} options={eligibleOptions}
                onChange={values=>change(index,{assignees:values.map(value=>{const [type,id]=value.split(':');return {type,id:Number(id)};}),allowEmpty:true})} />
            {row.allowEmpty&&!row.assignees.length&&<p className="uk-text-meta">This service's default taskers will be cleared.</p>}
          </div>)}
          <button type="button" disabled={busy || changes.length>=Math.min(30,options.length)} className="uk-button hms-btn hms-white-btn" onClick={addService}>+ Service</button>
        </> : <>
          <p role="status">{preview.plan.filter(cell=>cell.status==='change').length} changes · {preview.plan.filter(cell=>cell.status==='unchanged').length} unchanged</p>
          <p className="uk-text-meta">Review the current and new taskers. Saving replaces defaults for the listed services only. Existing tasks keep their assignments.</p>
          <div className="rbr-defaults-preview"><table className="uk-table uk-table-divider hms-table"><thead><tr><th>Property</th><th>Service</th><th>Current taskers</th><th>New taskers</th></tr></thead>
            <tbody>{preview.plan.map(cell=><tr key={`${cell.rental_id}/${cell.service_key}`}><td>{propertyLabel(cell.rental_id)}</td><td>{labels.get(cell.service_key)}</td><td>{names(cell.before)||'No taskers'}</td><td>{names(cell.after)||'No taskers'}</td></tr>)}</tbody></table></div>
        </>}
      </div>
      <div className="rbr-defaults-drawer-footer">
        {preview ? <><button type="button" disabled={busy} className="uk-button hms-btn hms-red-btn" onClick={onSave}>{busy?'Saving…':'Save Defaults'}</button>
          <button type="button" disabled={busy} className="uk-button uk-button-default" onClick={onBack}>Back to Edit</button></>
          : <button type="button" disabled={busy || !valid} className="uk-button hms-btn hms-red-btn" onClick={onPreview}>{busy?'Preparing…':'Preview Changes'}</button>}
        <button type="button" disabled={busy} className="uk-button hms-btn uk-button-default" onClick={onClose}>Cancel</button>
      </div>
    </div>
  </div>,document.body);
}
